// Seleção de linhas numa lista, com âncora de intervalo e cursor de teclado.
//
// Mora fora da tela porque a regra tem casos de borda que não se veem olhando o
// JSX — intervalo com Shift, âncora depois de desmarcar, seleção que sobrevive à
// troca de filtro — e cada um deles é uma forma de o curador decidir sobre
// registro diferente do que ele acha que escolheu.
//
// **A seleção SOBREVIVE à troca de filtro e de página**, e isso é decisão, não
// descuido. A alternativa seria limpá-la, e limpar em silêncio uma seleção de 300
// feições é caro; avisar antes de cada clique em filtro é ruído. Como a decisão em
// lote é enviada POR ID, ela não precisa que a linha esteja visível — o conjunto é
// um fato independente da janela que o mostra. O que a tela deve fazer, e faz, é
// dizer quantas das selecionadas estão na lista atual: assim nada se perde e nada
// fica escondido.

import { useCallback, useMemo, useRef, useState } from "react";

export type Selecao = {
  /** Os ids marcados, na ordem em que foram marcados não importa — é conjunto. */
  marcadas: ReadonlySet<string>;
  /** Quantas das marcadas estão na lista visível agora. */
  visiveisMarcadas: number;
  /** Todas as linhas visíveis estão marcadas? Falso para lista vazia. */
  paginaInteira: boolean;
  /** Alguma linha visível está marcada, mas não todas. */
  paginaParcial: boolean;
  /** Marca ou desmarca uma linha. Com `comShift`, marca o intervalo até a âncora. */
  alternar: (id: string, comShift?: boolean) => void;
  /** Marca todas as visíveis, ou desmarca todas as visíveis se já estiverem. */
  alternarPagina: () => void;
  limpar: () => void;
  /** Índice da linha com o cursor de teclado. `-1` = nenhuma. */
  foco: number;
  /** Move o cursor, preso aos limites da lista. */
  mover: (passo: number) => void;
  focar: (indice: number) => void;
};

export function useSelecao(ids: readonly string[]): Selecao {
  const [marcadas, setMarcadas] = useState<ReadonlySet<string>>(() => new Set());
  const [foco, setFoco] = useState(-1);

  // A âncora do Shift-clique: a última linha que o curador marcou ou desmarcou
  // sem Shift. Guardada em `ref` porque mudá-la não precisa redesenhar nada, e
  // porque um estado a mais aqui daria dois renders por clique.
  const ancora = useRef<string | null>(null);

  const alternar = useCallback(
    (id: string, comShift = false) => {
      setMarcadas((antes) => {
        const proximo = new Set(antes);
        const de = ancora.current === null ? -1 : ids.indexOf(ancora.current);
        const ate = ids.indexOf(id);

        // Intervalo: só quando há âncora E ela ainda está na lista. Sem esta
        // conferência, uma âncora de antes da troca de filtro daria `indexOf`
        // igual a −1 e o intervalo pegaria do começo da lista até aqui — muito
        // mais do que o curador apontou.
        if (comShift && de >= 0 && ate >= 0) {
          const [inicio, fim] = de <= ate ? [de, ate] : [ate, de];
          // Shift SOMA ao conjunto, nunca alterna: o gesto é "até aqui também",
          // e alternar faria um segundo Shift-clique desmarcar o que o primeiro
          // marcou, o que ninguém espera.
          for (let i = inicio; i <= fim; i++) proximo.add(ids[i]!);
          return proximo;
        }

        if (proximo.has(id)) proximo.delete(id);
        else proximo.add(id);
        // A âncora acompanha o último clique SIMPLES, marcando ou desmarcando: o
        // próximo Shift-clique parte de onde a mão estava.
        ancora.current = id;
        return proximo;
      });
      const i = ids.indexOf(id);
      if (i >= 0) setFoco(i);
    },
    [ids],
  );

  const visiveisMarcadas = useMemo(
    () => ids.reduce((n, id) => n + (marcadas.has(id) ? 1 : 0), 0),
    [ids, marcadas],
  );

  const alternarPagina = useCallback(() => {
    setMarcadas((antes) => {
      const proximo = new Set(antes);
      const todas = ids.length > 0 && ids.every((id) => proximo.has(id));
      // Desmarcar a página não limpa a seleção inteira: o que está marcado fora
      // da lista atual continua marcado, porque o conjunto é independente da
      // janela. Quem quer zerar usa "Limpar".
      for (const id of ids) {
        if (todas) proximo.delete(id);
        else proximo.add(id);
      }
      return proximo;
    });
  }, [ids]);

  const limpar = useCallback(() => {
    setMarcadas(new Set());
    ancora.current = null;
  }, []);

  const mover = useCallback(
    (passo: number) => {
      if (ids.length === 0) return;
      setFoco((antes) => {
        // Sem foco ainda: descer começa na primeira, subir começa na última.
        if (antes < 0) return passo > 0 ? 0 : ids.length - 1;
        return Math.min(ids.length - 1, Math.max(0, antes + passo));
      });
    },
    [ids],
  );

  const focar = useCallback(
    (indice: number) => setFoco(indice < 0 || indice >= ids.length ? -1 : indice),
    [ids],
  );

  return {
    marcadas,
    visiveisMarcadas,
    paginaInteira: ids.length > 0 && visiveisMarcadas === ids.length,
    paginaParcial: visiveisMarcadas > 0 && visiveisMarcadas < ids.length,
    alternar,
    alternarPagina,
    limpar,
    // O cursor nunca aponta para fora da lista: "carregar mais" só cresce, mas
    // trocar de filtro encurta, e um índice velho apontaria para a linha errada.
    foco: foco >= ids.length ? -1 : foco,
    mover,
    focar,
  };
}

/**
 * O alvo do evento é um campo de texto?
 *
 * Atalho de uma letra numa tela com campo de UF e campo de busca é armadilha
 * garantida: digitar "V" no filtro validaria uma feição. `select` entra na lista
 * porque ele consome setas para trocar de opção.
 */
export function ehCampoDeTexto(alvo: EventTarget | null): boolean {
  if (!(alvo instanceof HTMLElement)) return false;
  if (alvo.isContentEditable) return true;
  return ["INPUT", "TEXTAREA", "SELECT"].includes(alvo.tagName);
}
